import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import MuiAlert from '@mui/material/Alert';
import { RouteComponentProps, withRouter, useHistory } from 'react-router-dom';
import { FAKE_LOGIN_RESPONSE, UserContext } from './UserContext';
import { registerPushDevice, requestPushPermission } from '../pushDevice';

import {
  clearAuthSession,
  fetchUserMeProfile,
  getApiHost,
  isProd,
  renderBoilerPlate,
  setNavigationHistory,
} from './Common';
import { Button, Paper, Typography } from '@mui/material';

import { signInWithPopup, signOut } from 'firebase/auth';
import { auth, googleProvider, microsoftProvider } from '../firebaseApp';

const FIREBASE_POPUP_CANCELLED = new Set([
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
]);

declare global {
  interface Window {
    saferGoogleToken: any;
    saferAuthScheme: string | undefined;
    saferApiHost: any;
    saferUserOrg: string | undefined;
    saferLogoutInProgress: boolean;
  }
}

const Login: React.FC<RouteComponentProps> = () => {
  const { profile, handleLogin, handleLogout } = useContext(UserContext);
  const history = useHistory();
  // eslint-disable-next-line
  const [alert, setAlert] = useState(undefined);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const loginInProgressRef = useRef(false);
  const fakeResponse = FAKE_LOGIN_RESPONSE;

  const isAuth = useCallback(() => profile !== undefined, [profile]);
  const handleFakeLogin = () => handleLogin(fakeResponse);


  const performLogin = useCallback(({ tokenId, authScheme }) => {
    setAlert(undefined);
    getApiHost((apiHost) => {
      const apiUrl = `${apiHost}/v1/firebase_auth`;
      const apiInit = {
        headers: {
          Authorization: `Firebase ${tokenId}`,
        },
      };
      fetch(apiUrl, apiInit)
        .then(async (res) => {
          const authResponse = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error(
              authResponse.detail
              || authResponse.status
              || `HTTP ${res.status}`,
            );
          }
          return authResponse;
        })
        .then(authResponse => {
          setIsLoggingIn(false);
          if (authResponse.status === 'Valid') {
            fetchUserMeProfile(apiHost, authResponse, { tokenId, authScheme })
              .then((profileObj) => {
                handleLogin({
                  tokenId,
                  authScheme,
                  profileObj,
                });
                registerPushDevice(apiHost).catch((err) => {
                  console.warn('Push device registration failed:', err);
                });
                history.push('/') ;
              });
          }
          else {
            setAlert('Invalid authorization: ' + JSON.stringify(authResponse));
            clearAuthSession();
            handleLogin(undefined);
          }
        })
        .catch(e => {
          setIsLoggingIn(false);
          const errorStr = e?.message || JSON.stringify(e);
          console.log(`Login, handleLogin, fetch event error: ${errorStr}`);
          setAlert(`Login failure: ${errorStr}`);
          clearAuthSession();
          handleLogin(undefined);
        });
    });
  }, [handleLogin, history]);

  const firebaseSignIn = useCallback(async (provider, authScheme = 'Firebase') => {
    if (loginInProgressRef.current) {
      return;
    }
    loginInProgressRef.current = true;
    setAlert(undefined);
    setIsLoggingIn(true);
    try {
      // Must run before signInWithPopup — closing the auth popup ends the click gesture
      // and browsers silently leave permission as "default" without showing a prompt.
      await requestPushPermission();
      const result = await signInWithPopup(auth, provider);
      const tokenId = await result.user.getIdToken();
      performLogin({ tokenId, authScheme });
    } catch (error: any) {
      setIsLoggingIn(false);
      await signOut(auth).catch(() => {});
      if (!FIREBASE_POPUP_CANCELLED.has(error.code)) {
        console.error('Firebase login failed:', error);
        setAlert(error.message);
      }
    } finally {
      loginInProgressRef.current = false;
    }
  }, [performLogin]);

  const handleGoogleLogin = useCallback(() => {
    firebaseSignIn(googleProvider, 'Firebase');
  }, [firebaseSignIn]);

  const handleMicrosoftLogin = useCallback(() => {
    firebaseSignIn(microsoftProvider, 'Firebase');
  }, [firebaseSignIn]);


  useEffect(() => {
    setNavigationHistory(history);
  }, [history]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search.substring(1));
    const redir = params.get('redir');
    if (isAuth() && redir !== null) {
      history.push(redir);
    }
  }, [isAuth, history]);

  return renderBoilerPlate(
    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
      <div>
        <Paper>
          <Typography variant="subtitle1">Authentication</Typography>
          {isProd() ? (

            isAuth() ? (
              <Button variant='contained' color="secondary" onClick={handleLogout}>
                Logout
              </Button>
            ) : (<>
              <Button variant='contained' onClick={handleGoogleLogin} disabled={isLoggingIn}>
                Login (Google)
              </Button>
              <Button variant='contained' onClick={handleMicrosoftLogin} disabled={isLoggingIn} sx={{ml: 1}}>
                Login (Microsoft)
              </Button>
            </>)

          ) : (

            isAuth() ?
              <Button variant='contained' color="secondary" onClick={handleLogout}>
                Fake Logout
              </Button>
              :
              <Button variant='contained' onClick={handleFakeLogin}>
                Fake Login
              </Button>

          )}
        </Paper>
        {alert !== undefined && (
          <MuiAlert elevation={6} variant="filled" severity="error">
            Error: {alert}
          </MuiAlert>
        )}
      </div>
    </div>
  );
}

export default withRouter(Login);
